/**
 * La situation d'un compte, lue pour le rapprochement manuel.
 *
 * ## Pourquoi ces lectures existent
 *
 * Demandé le 18 septembre 2026, devant un virement dont 70,00 € restaient à
 * placer sans qu'on puisse dire pourquoi :
 *
 *   « Il faudrait une lecture simplifiée et, si possible la possibilité de
 *     voir tt le détail des factures, notes de crédits et versement pour
 *     analyser au mieux la situation du compte »
 *
 * L'écran de rapprochement montrait le virement, et rien de ce qui permet de
 * le juger : ni les factures citées, ni leurs notes de crédit, ni ce que la
 * famille a déjà versé. L'administration devait ouvrir un autre écran, y
 * recopier un numéro, revenir — et le faire cinq fois pour un virement qui
 * cite cinq factures.
 *
 * ## Ce qui n'est PAS fait ici
 *
 * Aucune vue nouvelle, aucune migration. Tout se lit dans `v_admin_factures`,
 * `v_invoice_credit_note_list`, `payment_allocations` et
 * `v_user_provision_balances`, qui existent et sont déjà éprouvées. Un panneau
 * de lecture ne justifie pas une vue de plus à tenir.
 *
 * Et rien n'est agrégé en SQL improvisé : les soldes affichés sont **ceux que
 * le reste de l'application affiche**. Un panneau qui recalculerait « reste
 * dû » à sa façon finirait par contredire l'écran des factures, et c'est
 * exactement le genre de contradiction qui fait perdre confiance dans les deux.
 */

import { db } from './db';
import { lireLesJumeaux } from './jumeaux.supabase';
import { chargerNotesDeCredit } from '../factures/gateway.supabase';
import { normalizeReference } from './communication';
import type { NoteDeCredit } from '../factures/types';
import type { FactureCitee, ReferenceCitee } from './facturesCitees';

/*
  Des garde-fous, pas des limites attendues. La famille la plus fournie de la
  production porte quelques dizaines de factures. S'ils étaient atteints,
  l'écran le DIT — il ne tronque pas en silence.
*/
const PLAFOND_FACTURES = 200;
const PLAFOND_VERSEMENTS = 200;
/** Autant de références citées que l'écran accepte de résoudre d'un coup. */
const PLAFOND_REFERENCES = 30;

const CHAMPS_FACTURE = `
  invoice_id, user_id, invoice_number, due_date, settlement,
  amount_cents, credit_notes_cents, credit_note_count, net_amount_cents,
  allocated_cents, outstanding_cents, overpaid_cents, last_payment_date,
  pdf_url, nom, prenom, email
`;

interface LigneFactureBrute {
  invoice_id: string;
  user_id: string | null;
  invoice_number: string;
  due_date: string | null;
  settlement: string;
  amount_cents: number;
  credit_notes_cents: number;
  credit_note_count: number;
  net_amount_cents: number;
  allocated_cents: number;
  outstanding_cents: number;
  overpaid_cents: number;
  last_payment_date: string | null;
  pdf_url: string | null;
  nom: string | null;
  prenom: string | null;
  email: string | null;
}

function etiquette(nom: string | null, prenom: string | null): string {
  return [prenom, nom].filter(Boolean).join(' ').trim() || 'Compte sans nom';
}

/**
 * Les factures désignées par les références qu'un virement cite.
 *
 * ## Pourquoi la recherche porte sur les sept chiffres
 *
 * Un parent écrit `CDV-WBK-2500766`, `WBK-2500766` ou `2500766` — les trois
 * désignent la même facture, et le rapprochement automatique les traite déjà
 * comme équivalents. Chercher le numéro complet ici rendrait « introuvable »
 * une facture que la machine, elle, avait trouvée.
 *
 * Mesuré en production le 18 septembre 2026 : **aucun groupe de sept chiffres
 * n'est partagé par deux factures**. La recherche par chiffres est donc sans
 * ambiguïté aujourd'hui. Elle pourrait cesser de l'être — d'où le départage
 * par le tag cité, qui ne coûte rien et qui, le jour venu, choisira
 * `CDV-WBK-2500766` plutôt que `CDV-NDSC-2500766`.
 */
export async function lireFacturesCitees(
  references: ReferenceCitee[],
  paymentId: string,
): Promise<FactureCitee[]> {
  const refs = references.slice(0, PLAFOND_REFERENCES);
  if (refs.length === 0) return [];

  const { data, error } = await db
    .from('v_admin_factures')
    .select(CHAMPS_FACTURE)
    /* Égalité pour le format historique : `CDV-250530-00010` ne contient pas
       ses sept derniers chiffres, un tiret les coupe. Le `ilike` ne rendrait
       rien. */
    .or(refs.map((r) => (r.numeroComplet
      ? `invoice_number.eq.${r.numeroComplet}`
      : `invoice_number.ilike.*${r.digits}*`)).join(','))
    .limit(PLAFOND_REFERENCES * 2);
  if (error) throw error;

  const lignes = (data ?? []) as unknown as LigneFactureBrute[];

  // Ce que CE virement a déjà donné, facture par facture. Sans cette colonne,
  // une facture soldée par ce virement même se lit comme « déjà payée par
  // ailleurs » — et l'administration défait ce qu'elle vient de faire.
  const donnees = new Map<string, number>();
  if (lignes.length > 0) {
    const { data: a, error: erreurA } = await db
      .from('payment_allocations')
      .select('invoice_id, amount_cents')
      .eq('payment_id', paymentId)
      .in('invoice_id', lignes.map((l) => l.invoice_id));
    if (erreurA) throw erreurA;
    for (const l of (a ?? []) as unknown as { invoice_id: string; amount_cents: number }[]) {
      donnees.set(l.invoice_id, (donnees.get(l.invoice_id) ?? 0) + Number(l.amount_cents));
    }
  }

  return refs.map((r) => {
    const candidates = lignes.filter((l) => (r.numeroComplet
      ? l.invoice_number === r.numeroComplet
      : l.invoice_number.includes(r.digits)));
    const ligne = candidates.length > 1
      ? candidates.find((l) => normalizeReference(l.invoice_number).includes(r.tag)) ?? candidates[0]
      : candidates[0];

    if (!ligne) {
      return {
        digits: r.digits, origine: r.origine,
        invoiceId: null, invoiceNumber: null, pdfUrl: null,
        userId: null, famille: null, settlement: null,
        factureCents: 0, notesCents: 0, netCents: 0,
        recuCents: 0, resteCents: 0, deCeVirementCents: 0,
      } satisfies FactureCitee;
    }

    return {
      digits: r.digits,
      origine: r.origine,
      invoiceId: ligne.invoice_id,
      invoiceNumber: ligne.invoice_number,
      pdfUrl: ligne.pdf_url,
      userId: ligne.user_id,
      famille: etiquette(ligne.nom, ligne.prenom),
      settlement: ligne.settlement,
      factureCents: Number(ligne.amount_cents),
      notesCents: Number(ligne.credit_notes_cents),
      netCents: Number(ligne.net_amount_cents),
      recuCents: Number(ligne.allocated_cents),
      resteCents: Number(ligne.outstanding_cents),
      deCeVirementCents: donnees.get(ligne.invoice_id) ?? 0,
    } satisfies FactureCitee;
  });
}

/** Une facture du compte, avec le détail de ses notes de crédit. */
export interface FactureDuCompte {
  invoiceId: string;
  invoiceNumber: string;
  echeanceLe: string | null;
  settlement: string;
  factureCents: number;
  notesCents: number;
  notesCount: number;
  netCents: number;
  recuCents: number;
  resteCents: number;
  tropPercuCents: number;
  dernierPaiement: string | null;
  pdfUrl: string | null;
  notes: NoteDeCredit[];
}

/** Un versement reçu par ce compte, ou une part de versement. */
export interface VersementDuCompte {
  allocationId: string;
  paymentId: string;
  dateValeur: string | null;
  /** Ce que cette ligne a placé. */
  montantCents: number;
  /** Ce que le virement portait en tout — souvent davantage. */
  virementCents: number;
  cible: string;
  methode: string | null;
  notes: string | null;
  invoiceNumber: string | null;
  communication: string | null;
  contrepartie: string | null;
  iban: string | null;
  /** Vrai quand cette ligne appartient au virement en cours d'examen. */
  deCeVirement: boolean;
}

/**
 * Un virement de cette famille qui n'est encore placé nulle part.
 *
 * ## Le défaut que cette liste corrige
 *
 * Signalé le 18 septembre 2026 : « moi dans ma compta j'ai 3 paiements qui
 * sont arrivés pour ce compte... si je comprends bien il trouve 4 x 98 euros
 * pour ce compte ». Le panneau annonçait « Versements reçus (3) » et s'arrêtait
 * là — les virements de la MÊME famille restés en file n'y figuraient pas.
 *
 * Le compte paraissait donc complet alors qu'il taisait 567,50 € de virements
 * de cette famille. **Un compte qui ne montre qu'une partie de ce qu'une
 * famille a versé est pire qu'un compte sans versements** : on en tire un
 * solde, et le solde est faux.
 *
 * Mesuré en production le 18 septembre 2026 : **271 familles** ont ainsi
 * **1 048 virements en file, 43 529,17 €**, invisibles depuis leur compte.
 */
export interface VirementEnFile {
  paymentId: string;
  dateValeur: string;
  /** Ce que le virement portait. */
  montantCents: number;
  /** Ce qu'il reste à placer dessus. */
  resteCents: number;
  communication: string | null;
  contrepartie: string | null;
  iban: string | null;
  /** Vrai pour le virement que l'écran examine déjà. */
  examine: boolean;
  /**
   * Vrai quand une ligne de l'autre source (relevé CODA ↔ export CSV) décrit
   * le même mouvement : très probablement le même virement lu deux fois.
   * Le 27/09/2026, 173,00 € en file annonçaient « 173,00 € de trop » à une
   * famille qui n'avait payé qu'une fois.
   */
  luDeuxFois: boolean;
}

export interface CompteFamille {
  userId: string;
  famille: string;
  courriel: string | null;
  factures: FactureDuCompte[];
  versements: VersementDuCompte[];
  /** Ce que cette famille a versé et qui n'est placé nulle part. */
  enFile: VirementEnFile[];
  enFileCents: number;
  /** L'avoir tel que la famille le voit, verrou compris. `null` s'il est nul. */
  avoir: {
    avoirCents: number;
    disponibleCents: number;
    enAttenteCents: number;
    etat: string | null;
  } | null;
  /** Ce que ce compte réclame encore, annulées exclues. */
  resteDuCents: number;
  /** Vrai si un plafond a été atteint : l'écran le dit plutôt que de mentir. */
  tronque: boolean;
}

/**
 * Tout ce qu'il faut pour juger un compte : ses factures, leurs notes de
 * crédit, les versements reçus, et l'avoir.
 *
 * Les factures **annulées** sont conservées et affichées comme telles. Les
 * masquer ferait disparaître la raison pour laquelle une famille a un
 * excédent, ce qui est précisément la question posée.
 */
export async function lireCompteFamille(
  userId: string,
  paiementExamine?: string,
  /** Le compte bancaire du virement examiné : il entre dans la recherche. */
  ibanExamine?: string | null,
): Promise<CompteFamille> {
  const { data, error } = await db
    .from('v_admin_factures')
    .select(CHAMPS_FACTURE)
    .eq('user_id', userId)
    .order('due_date', { ascending: false, nullsFirst: false })
    .order('invoice_id', { ascending: true })
    .limit(PLAFOND_FACTURES);
  if (error) throw error;

  const lignes = (data ?? []) as unknown as LigneFactureBrute[];
  const identite = lignes[0];
  const notes = await chargerNotesDeCredit(lignes.map((l) => l.invoice_id));

  const factures: FactureDuCompte[] = lignes.map((l) => ({
    invoiceId: l.invoice_id,
    invoiceNumber: l.invoice_number,
    echeanceLe: l.due_date,
    settlement: l.settlement,
    factureCents: Number(l.amount_cents),
    notesCents: Number(l.credit_notes_cents),
    notesCount: Number(l.credit_note_count),
    netCents: Number(l.net_amount_cents),
    recuCents: Number(l.allocated_cents),
    resteCents: Number(l.outstanding_cents),
    tropPercuCents: Number(l.overpaid_cents),
    dernierPaiement: l.last_payment_date,
    pdfUrl: l.pdf_url,
    notes: notes.get(l.invoice_id) ?? [],
  }));

  const [versements, avoir, nom, rattaches] = await Promise.all([
    lireVersements(userId, lignes.map((l) => l.invoice_id), paiementExamine),
    lireAvoir(userId),
    identite ? Promise.resolve(etiquette(identite.nom, identite.prenom)) : nomDuCompte(userId),
    lireComptesRattaches(userId),
  ]);

  const enFile = await lireEnFile(
    [...versements.map((v) => v.iban), ...rattaches, ibanExamine ?? null],
    paiementExamine,
  );

  return {
    userId,
    famille: nom,
    courriel: identite?.email ?? null,
    factures,
    versements,
    enFile,
    enFileCents: enFile.reduce((s, v) => s + v.resteCents, 0),
    avoir,
    /* Une facture annulée ne réclame rien à personne, même quand son solde
       reste inscrit faute de note de crédit. C'est la règle de l'écran des
       factures, tenue ici à l'identique. */
    resteDuCents: factures.reduce(
      (s, f) => (f.settlement === 'cancelled' ? s : s + f.resteCents), 0,
    ),
    tronque: lignes.length >= PLAFOND_FACTURES || versements.length >= PLAFOND_VERSEMENTS,
  };
}

/**
 * Les comptes bancaires que l'administration a explicitement rattachés à cette
 * famille, par le bouton « Rattacher ce compte ».
 */
async function lireComptesRattaches(userId: string): Promise<string[]> {
  const { data, error } = await db
    .from('payment_counterparties')
    .select('iban')
    .eq('user_id', userId)
    .limit(20);
  if (error) throw error;
  return ((data ?? []) as unknown as { iban: string }[]).map((l) => l.iban);
}

/**
 * Les virements de cette famille qui ne sont placés nulle part.
 *
 * ## Comment on sait qu'un virement en file est « de cette famille »
 *
 * Par son compte bancaire, et de trois façons qui se cumulent :
 *
 * 1. un compte d'où sont venus des versements **déjà affectés** à cette
 *    famille ;
 * 2. un compte **rattaché explicitement** par l'administration ;
 * 3. le compte du **virement examiné**, quand l'écran en examine un.
 *
 * La troisième n'est pas un luxe : le cas qui a fait écrire cette fonction est
 * une famille qui verse depuis TROIS comptes — le sien, celui du père, et un
 * troisième. Les virements du premier n'avaient jamais rien soldé ici, donc
 * aucune affectation ne le désignait, donc les deux premières règles le
 * manquaient — et c'est précisément le compte du virement qu'on examinait.
 *
 * Le rapprochement, lui, ne conclut rien de tout cela : ces virements restent
 * en file. Cette liste ne les place pas, elle les rend VISIBLES depuis le
 * compte, pour qu'on ne lise plus « 3 versements » quand la famille en a fait
 * quatre.
 */
async function lireEnFile(
  ibans: (string | null)[],
  paiementExamine?: string,
): Promise<VirementEnFile[]> {
  const comptes = [...new Set(ibans.filter((i): i is string => !!i))];
  if (comptes.length === 0) return [];

  const { data, error } = await db
    .from('v_payment_allocation_state')
    .select(`payment_id, source, value_date, amount_cents, unallocated_cents,
             communication_raw, structured_digits, counterparty_iban, counterparty_name`)
    .in('counterparty_iban', comptes)
    .gt('unallocated_cents', 0)
    .order('value_date', { ascending: false })
    .limit(PLAFOND_VERSEMENTS);
  if (error) throw error;

  const lignes = (data ?? []) as unknown as {
    payment_id: string; source: string; value_date: string; amount_cents: number;
    unallocated_cents: number; communication_raw: string | null; structured_digits: string | null;
    counterparty_iban: string | null; counterparty_name: string | null;
  }[];

  /* Une lecture de plus, et qui ne doit pas faire tomber le compte : sans elle,
     les lignes s'affichent quand même, sans le drapeau. */
  let jumeaux = new Map<string, unknown>();
  try {
    jumeaux = await lireLesJumeaux('entree', lignes.map((l) => ({
      id: l.payment_id, valueDate: l.value_date, amountCents: Number(l.amount_cents),
    })));
  } catch {
    jumeaux = new Map();
  }

  return lignes.map((l) => ({
    paymentId: l.payment_id,
    dateValeur: l.value_date,
    montantCents: Number(l.amount_cents),
    resteCents: Number(l.unallocated_cents),
    communication: l.communication_raw,
    contrepartie: l.counterparty_name,
    iban: l.counterparty_iban,
    examine: l.payment_id === paiementExamine,
    luDeuxFois: jumeaux.has(l.payment_id),
  }));
}

/**
 * Les versements reçus par ce compte : ceux placés sur ses factures, et ceux
 * versés sur sa provision.
 *
 * Deux lectures plutôt qu'une jointure imbriquée : une allocation de provision
 * n'a pas de facture, et un `!inner` sur `invoices` l'aurait donc écartée en
 * silence. C'est justement l'argent le plus difficile à retrouver.
 */
async function lireVersements(
  userId: string,
  invoiceIds: string[],
  paiementExamine?: string,
): Promise<VersementDuCompte[]> {
  const CHAMPS = `id, amount_cents, target, method, notes, created_at, payment_id,
                  invoices(invoice_number),
                  payments(value_date, amount_cents, communication_raw,
                           counterparty_name, counterparty_iban)`;

  const requetes = [
    db.from('payment_allocations').select(CHAMPS)
      .eq('provision_user_id', userId).limit(PLAFOND_VERSEMENTS),
  ];
  if (invoiceIds.length > 0) {
    requetes.push(
      db.from('payment_allocations').select(CHAMPS)
        .in('invoice_id', invoiceIds).limit(PLAFOND_VERSEMENTS),
    );
  }

  const reponses = await Promise.all(requetes);
  const parLigne = new Map<string, VersementDuCompte>();

  for (const { data, error } of reponses) {
    if (error) throw error;
    for (const l of (data ?? []) as unknown as Record<string, unknown>[]) {
      const p = l.payments as {
        value_date?: string; amount_cents?: number;
        communication_raw?: string | null; counterparty_name?: string | null;
        counterparty_iban?: string | null;
      } | null;
      parLigne.set(String(l.id), {
        allocationId: String(l.id),
        paymentId: String(l.payment_id),
        dateValeur: p?.value_date ?? null,
        montantCents: Number(l.amount_cents),
        virementCents: Number(p?.amount_cents ?? 0),
        cible: String(l.target),
        methode: (l.method as string) ?? null,
        notes: (l.notes as string) ?? null,
        invoiceNumber: (l.invoices as { invoice_number: string } | null)?.invoice_number ?? null,
        communication: p?.communication_raw ?? null,
        contrepartie: p?.counterparty_name ?? null,
        iban: p?.counterparty_iban ?? null,
        deCeVirement: String(l.payment_id) === paiementExamine,
      });
    }
  }

  return [...parLigne.values()].sort(
    (a, b) => (b.dateValeur ?? '').localeCompare(a.dateValeur ?? '')
      || a.allocationId.localeCompare(b.allocationId),
  );
}

/**
 * L'avoir de la famille, tel qu'elle le voit.
 *
 * `available_cents` est **verrouillé** depuis le 18 septembre 2026 : tant que
 * `provision_auto` vaut `off`, il rend zéro quel que soit l'avoir possédé.
 * Les deux colonnes sont donc lues, et l'écran montre les deux — afficher le
 * seul disponible ferait croire que l'argent n'existe pas.
 */
async function lireAvoir(userId: string): Promise<CompteFamille['avoir']> {
  const { data, error } = await db
    .from('v_user_provision_balances')
    .select('avoir_cents, available_cents, pending_cents, verification_etat')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const l = data as unknown as {
    avoir_cents: number; available_cents: number;
    pending_cents: number; verification_etat: string | null;
  };
  const avoirCents = Number(l.avoir_cents ?? 0);
  const enAttenteCents = Number(l.pending_cents ?? 0);
  if (avoirCents === 0 && enAttenteCents === 0) return null;

  return {
    avoirCents,
    disponibleCents: Number(l.available_cents ?? 0),
    enAttenteCents,
    etat: l.verification_etat ?? null,
  };
}

/** Le nom d'un compte sans facture : il en existe, et ils ont un nom. */
async function nomDuCompte(userId: string): Promise<string> {
  const { data, error } = await db
    .from('users').select('nom, prenom').eq('id', userId).maybeSingle();
  if (error) throw error;
  const u = (data ?? {}) as { nom?: string | null; prenom?: string | null };
  return etiquette(u.nom ?? null, u.prenom ?? null);
}
